import { supabaseClient } from '@/configs/supabase'
import {
  IPaymentEntry,
  IBatchPaymentSaveInput,
  PaymentMethod,
} from './dailySalesSummary.interface'
import { getOrCreateDailyCustomerSummary } from './dailySalesSummary'

const handleError = (error: any, message: string) => {
  console.error(message, error)
  return null
}

// ============================================================
// 4.1 - Basic CRUD Operations for PaymentEntry
// Requirements: 9.1, 9.2, 9.3, 9.4
// ============================================================

/**
 * Create a new PaymentEntry record linked to a DailyCustomerSummary
 * Requirements: 9.1
 */
export const createPaymentEntry = async (
  entry: Omit<IPaymentEntry, 'id' | 'createdAt' | 'updatedAt'>
): Promise<IPaymentEntry | null> => {
  try {
    const entryId = crypto.randomUUID()
    const now = new Date().toISOString()

    const { data, error } = await supabaseClient
      .from('PaymentEntry')
      .insert({
        id: entryId,
        daily_customer_summary_id: entry.dailyCustomerSummaryId,
        order_book_id: entry.orderBookId ?? null,
        amount_thb: entry.amountTHB,
        payment_method: entry.paymentMethod,
        note: entry.note || null,
        created_at: now,
        updated_at: now,
      })
      .select()
      .single()

    if (error) {
      return handleError(error, 'Error creating PaymentEntry:')
    }

    return mapRowToPaymentEntry(data)
  } catch (error) {
    return handleError(error, 'Error in createPaymentEntry:')
  }
}


/**
 * Update an existing PaymentEntry record
 * Requirements: 9.2
 */
export const updatePaymentEntry = async (
  id: string,
  updates: Partial<Pick<IPaymentEntry, 'amountTHB' | 'paymentMethod' | 'note'>>
): Promise<IPaymentEntry | null> => {
  try {
    const updatePayload: Record<string, any> = {
      updated_at: new Date().toISOString(),
    }

    if (updates.amountTHB !== undefined) {
      updatePayload.amount_thb = updates.amountTHB
    }
    if (updates.paymentMethod !== undefined) {
      updatePayload.payment_method = updates.paymentMethod
    }
    if (updates.note !== undefined) {
      updatePayload.note = updates.note
    }

    const { data, error } = await supabaseClient
      .from('PaymentEntry')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      return handleError(error, 'Error updating PaymentEntry:')
    }

    return mapRowToPaymentEntry(data)
  } catch (error) {
    return handleError(error, 'Error in updatePaymentEntry:')
  }
}

/**
 * Delete a PaymentEntry record by id
 * Requirements: 9.3
 */
export const deletePaymentEntry = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabaseClient
      .from('PaymentEntry')
      .delete()
      .eq('id', id)

    if (error) {
      handleError(error, 'Error deleting PaymentEntry:')
      return false
    }

    return true
  } catch (error) {
    handleError(error, 'Error in deletePaymentEntry:')
    return false
  }
}

/**
 * Fetch all PaymentEntry records for a given DailyCustomerSummary
 * Requirements: 9.4
 */
export const getPaymentEntriesByDailyCustomerSummary = async (
  dailyCustomerSummaryId: string
): Promise<IPaymentEntry[]> => {
  try {
    const { data, error } = await supabaseClient
      .from('PaymentEntry')
      .select('*')
      .eq('daily_customer_summary_id', dailyCustomerSummaryId)
      .order('created_at', { ascending: true })

    if (error) {
      handleError(error, 'Error fetching PaymentEntries:')
      return []
    }

    return (data || []).map(mapRowToPaymentEntry)
  } catch (error) {
    handleError(error, 'Error in getPaymentEntriesByDailyCustomerSummary:')
    return []
  }
}


// ============================================================
// 4.2 - Recalculate DailyCustomerSummary Aggregates
// Requirements: 5.2, 9.5
// ============================================================

/**
 * Recalculate amountPaidTHB, outstandingBalanceTHB, paymentCount
 * from all PaymentEntry records and update DailyCustomerSummary
 * Requirements: 5.2, 9.5
 */
export const recalculateDailyCustomerSummaryAggregates = async (
  dailyCustomerSummaryId: string
): Promise<boolean> => {
  try {
    // Fetch all PaymentEntry records for this summary
    const { data: entries, error: fetchError } = await supabaseClient
      .from('PaymentEntry')
      .select('amount_thb')
      .eq('daily_customer_summary_id', dailyCustomerSummaryId)

    if (fetchError) {
      handleError(fetchError, 'Error fetching PaymentEntries for recalculation:')
      return false
    }

    // Calculate aggregates
    const paymentEntries = entries || []
    const amountPaidTHB = paymentEntries.reduce(
      (sum, entry) => sum + (entry.amount_thb || 0),
      0
    )
    const paymentCount = paymentEntries.length

    // Fetch the DailyCustomerSummary to get totalAmountTHB
    const { data: summary, error: summaryError } = await supabaseClient
      .from('DailyCustomerSummary')
      .select('total_amount_thb')
      .eq('id', dailyCustomerSummaryId)
      .single()

    if (summaryError) {
      handleError(summaryError, 'Error fetching DailyCustomerSummary for recalculation:')
      return false
    }

    const totalAmountTHB = summary?.total_amount_thb || 0
    const outstandingBalanceTHB = Math.max(0, totalAmountTHB - amountPaidTHB)

    // Update DailyCustomerSummary with recalculated values
    const { error: updateError } = await supabaseClient
      .from('DailyCustomerSummary')
      .update({
        amount_paid_thb: amountPaidTHB,
        outstanding_balance_thb: outstandingBalanceTHB,
        payment_count: paymentCount,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dailyCustomerSummaryId)

    if (updateError) {
      handleError(updateError, 'Error updating DailyCustomerSummary aggregates:')
      return false
    }

    return true
  } catch (error) {
    handleError(error, 'Error in recalculateDailyCustomerSummaryAggregates:')
    return false
  }
}


// ============================================================
// 4.3 - Batch Save PaymentEntries
// Requirements: 5.1, 9.5, 9.6
// ============================================================

/**
 * Batch save PaymentEntries: process adds, updates, and deletes in one action.
 * After all operations, recalculate DailyCustomerSummary aggregates.
 * Returns false if any operation fails.
 * Requirements: 5.1, 9.5, 9.6
 */
export const batchSavePaymentEntries = async (
  input: IBatchPaymentSaveInput
): Promise<boolean> => {
  try {
    const { customerId, date, orderBookId, adds, updates, deletes } = input

    // Get or create DailyCustomerSummary to ensure it exists
    const summary = await getOrCreateDailyCustomerSummary(date, customerId)
    if (!summary) {
      console.error('Failed to get or create DailyCustomerSummary for batch save')
      return false
    }

    const dailyCustomerSummaryId = summary.id

    // Process DELETEs
    for (const deleteId of deletes) {
      const success = await deletePaymentEntry(deleteId)
      if (!success) {
        console.error(`Failed to delete PaymentEntry: ${deleteId}`)
        return false
      }
    }

    // Process INSERTs (adds)
    for (const add of adds) {
      const result = await createPaymentEntry({
        dailyCustomerSummaryId,
        orderBookId: add.orderBookId ?? orderBookId,
        amountTHB: add.amountTHB,
        paymentMethod: add.paymentMethod,
        note: add.note,
      })
      if (!result) {
        console.error('Failed to create PaymentEntry in batch')
        return false
      }
    }

    // Process UPDATEs
    for (const update of updates) {
      const result = await updatePaymentEntry(update.id, {
        amountTHB: update.amountTHB,
        paymentMethod: update.paymentMethod,
        note: update.note,
      })
      if (!result) {
        console.error(`Failed to update PaymentEntry: ${update.id}`)
        return false
      }
    }

    // Recalculate aggregates after all operations
    const recalculated = await recalculateDailyCustomerSummaryAggregates(dailyCustomerSummaryId)
    if (!recalculated) {
      console.error('Failed to recalculate aggregates after batch save')
      return false
    }

    return true
  } catch (error) {
    handleError(error, 'Error in batchSavePaymentEntries:')
    return false
  }
}

// ============================================================
// Helper: Map Supabase row (snake_case) to IPaymentEntry (camelCase)
// ============================================================

const mapRowToPaymentEntry = (row: any): IPaymentEntry => ({
  id: row.id,
  dailyCustomerSummaryId: row.daily_customer_summary_id,
  orderBookId: row.order_book_id ?? undefined,
  amountTHB: row.amount_thb,
  paymentMethod: row.payment_method as PaymentMethod,
  note: row.note || undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})
